/**
 * The BMS domain errors every leaf raises.
 *
 * DOMAIN, NOT WIRE. A service and a repository fail with these; the handler
 * maps each onto its wire twin in `../presentation/errors.ts`, which is what a
 * contract declares. The two halves are deliberately different classes with
 * different tags, so a domain error carrying a `cause` can never be rendered
 * to a browser by accident: the boundary only passes through the tags the
 * contract declared, and no contract declares these.
 *
 * `BmsNotFoundError` is the ONLY answer to a cross-org read. Not forbidden,
 * not a 403: telling a caller that a record exists but belongs to someone else
 * is an existence oracle over another firm's book (multi-tenancy-safety.md).
 */
import { Schema as S } from "effect"

/** The record is not in this organisation's book. Also the cross-org answer. */
export class BmsNotFoundError extends S.TaggedError<BmsNotFoundError>()("BmsNotFoundError", {
  message: S.String,
  /** The noun the caller asked for, for the message. Never a field value. */
  resource: S.String,
}) {}

/**
 * The caller's `expectedVersion` is behind the row. Carries the row's current
 * version so the client can re-read and retry without a second round trip
 * (convention 3, the integer `version` column migration 015 adds).
 */
export class BmsVersionConflictError extends S.TaggedError<BmsVersionConflictError>()(
  "BmsVersionConflictError",
  {
    message: S.String,
    currentVersion: S.Int,
    /**
     * Every stale row of a write over several, each with its current version:
     * a batch is refused whole, so the caller is told every row to re-read at
     * once. Absent on a single-row write, where `currentVersion` says it all.
     */
    conflicts: S.optional(S.Array(S.Struct({ id: S.String, currentVersion: S.Int }))),
  },
) {}

/**
 * The same idempotency key was replayed with a DIFFERENT request body. Never
 * an overwrite: the first write stands and the caller is told (convention 4).
 */
export class BmsIdempotencyConflictError extends S.TaggedError<BmsIdempotencyConflictError>()(
  "BmsIdempotencyConflictError",
  {
    message: S.String,
    namespace: S.String,
  },
) {}

/** One `path` per offending field, so the wire `details[]` names where it went wrong. */
export const BmsValidationDetail = S.Struct({
  path: S.String,
  message: S.String,
}).annotations({ identifier: "BmsValidationDetail" })
export type BmsValidationDetail = typeof BmsValidationDetail.Type

/** A payload the schema accepted but the domain rejects. */
export class BmsValidationError extends S.TaggedError<BmsValidationError>()("BmsValidationError", {
  message: S.String,
  details: S.Array(BmsValidationDetail),
}) {}

/**
 * The organisation has no region configured and this operation reads one.
 *
 * Fails CLOSED, always. There is no default region and no fallback to any
 * country: a tax computation or a currency chosen from the absence of
 * configuration is a wrong number in a broker's ledger
 * (docs/BMS/reference/general/one-book-any-country.md).
 */
export class BmsRegionUnsetError extends S.TaggedError<BmsRegionUnsetError>()(
  "BmsRegionUnsetError",
  {
    message: S.String,
  },
) {}

/**
 * The principal is inside the right organisation but may not do this.
 *
 * Distinct from `BmsNotFoundError` on purpose, and only usable where the
 * caller ALREADY knows the record exists: a revert on an event the reader can
 * see, a scope the credential was not granted. Never for a record the caller
 * could not otherwise read.
 */
export class BmsForbiddenError extends S.TaggedError<BmsForbiddenError>()("BmsForbiddenError", {
  message: S.String,
}) {}
